import { isRole, roleHasCapability } from '@qim/shared'
import { ACCOUNT_TABS, CUSTOMER_TABS, DEPLOYMENT_TABS, FLEET_TABS, pathFor } from './routing.js'
import type { Capability } from '@qim/shared'
import type { AccountTab, CustomerTab, DeploymentTab, FleetTab, View } from './routing.js'

/**
 * Which tabs a screen offers, and to whom — the secondary navigation's rules.
 *
 * ## Capability, never a role name
 *
 * A tab names the capability its content needs, and is offered to a viewer
 * whose role holds it. That is the same rule the main nav already follows for
 * Audit and Access, and the same reason: not a control — every route checks
 * regardless — but a tab whose content always answers 403 is a worse way to
 * say no than no tab. `Record<Tab, …>` means a tab added to `routing.ts` has to
 * say what it needs here before this compiles.
 *
 * ## Why a tab with no `needs` still exists
 *
 * Its content is reachable by the floor every role builds on
 * (`deployment:read` / `account:read`), so there is nothing to withhold.
 *
 * ## Only one tab is no tabs
 *
 * A viewer offered a single tab sees no navigation at all: a bar with one item
 * in it is chrome that says nothing.
 */

export interface TabSpec {
  readonly label: string
  /** The capability the tab's content reads with. Absent: the floor. */
  readonly needs?: Capability
}

export const FLEET_TAB_SPECS: Readonly<Record<FleetTab, TabSpec>> = {
  instances: { label: 'Instances' },
  // `GET /cost/daily` and `GET /budget` both carry `cost:read`.
  cost: { label: 'Cost', needs: 'cost:read' },
}

export const CUSTOMER_TAB_SPECS: Readonly<Record<CustomerTab, TabSpec>> = {
  overview: { label: 'Overview' },
  // `GET …/approvals` is `deployment:read`: anyone who can see the customer can
  // see what is waiting. Deciding is `deployment:approve`, checked inside.
  approvals: { label: 'Approvals' },
  users: { label: 'Users', needs: 'users:manage' },
  cost: { label: 'Cost', needs: 'cost:read' },
  // `GET /tenants/{tenantId}/audit`.
  audit: { label: 'Audit', needs: 'audit:read' },
}

export const ACCOUNT_TAB_SPECS: Readonly<Record<AccountTab, TabSpec>> = {
  onboarding: { label: 'Onboarding' },
  // The kept scan and a re-scan are both `diagnostics:read`.
  instances: { label: 'Instances', needs: 'diagnostics:read' },
  cost: { label: 'Cost', needs: 'cost:read' },
  // The settings are on the account listing `account:read` returns; changing
  // them is `account:settings`, which the panel checks for its own control.
  settings: { label: 'Settings' },
}

export const DEPLOYMENT_TAB_SPECS: Readonly<Record<DeploymentTab, TabSpec>> = {
  overview: { label: 'Overview' },
  // Running the checks and reading past runs are both `diagnostics:run` (IM-199).
  debug: { label: 'Debug', needs: 'diagnostics:run' },
}

export const mayUse = (spec: TabSpec, role: string): boolean =>
  spec.needs === undefined || (isRole(role) && roleHasCapability(role, spec.needs))

export interface SubnavItem {
  readonly tab: string
  readonly label: string
  readonly to: string
}

export interface SubnavModel {
  /** The `<nav>`'s accessible name, so two landmarks are told apart. */
  readonly label: string
  /** The tabs this viewer is offered, in order. */
  readonly items: readonly SubnavItem[]
  /** The tab the path names, offered or not. */
  readonly current: string
  /**
   * Present when the path names a tab this viewer is not offered — a deep link
   * pasted by someone with a wider role. Rendered as the console's polite
   * refusal, with the way back, rather than as a crash or a blank.
   */
  readonly blocked?: { readonly needs: Capability; readonly fallback: SubnavItem }
}

const build = <T extends string>(
  label: string,
  tabs: readonly T[],
  specs: Readonly<Record<T, TabSpec>>,
  current: T,
  to: (tab: T) => string,
  role: string,
): SubnavModel => {
  const items = tabs
    .filter((tab) => mayUse(specs[tab], role))
    .map((tab) => ({ tab, label: specs[tab].label, to: to(tab) }))
  const needs = specs[current].needs
  const fallback = items[0]
  return {
    label,
    items,
    current,
    ...(!mayUse(specs[current], role) && needs !== undefined && fallback !== undefined
      ? { blocked: { needs, fallback } }
      : {}),
  }
}

/** The secondary navigation for a view, or undefined for a screen with none. */
export const subnavFor = (view: View, role: string): SubnavModel | undefined => {
  switch (view.screen) {
    case 'fleet':
      return build(
        'Fleet sections',
        FLEET_TABS,
        FLEET_TAB_SPECS,
        view.tab ?? FLEET_TABS[0],
        (tab) => pathFor({ screen: 'fleet', tab }),
        role,
      )
    case 'customer':
      return build(
        'Customer sections',
        CUSTOMER_TABS,
        CUSTOMER_TAB_SPECS,
        view.tab ?? CUSTOMER_TABS[0],
        (tab) => pathFor({ screen: 'customer', tenantId: view.tenantId, tab }),
        role,
      )
    case 'account':
      return build(
        'Account sections',
        ACCOUNT_TABS,
        ACCOUNT_TAB_SPECS,
        view.tab ?? ACCOUNT_TABS[0],
        (tab) =>
          pathFor({ screen: 'account', tenantId: view.tenantId, accountKey: view.accountKey, tab }),
        role,
      )
    case 'deployment':
      return build(
        'Instance sections',
        DEPLOYMENT_TABS,
        DEPLOYMENT_TAB_SPECS,
        view.tab ?? DEPLOYMENT_TABS[0],
        (tab) =>
          pathFor({
            screen: 'deployment',
            tenantId: view.tenantId,
            deploymentId: view.deploymentId,
            tab,
          }),
        role,
      )
    default:
      return undefined
  }
}
